import { useMsal } from '@azure/msal-react';

/**
 * User information interface for UI display
 */
interface UserInfo {
  displayName: string;
  email: string;
  isAuthenticated: boolean;
}

/**
 * Extracts the first name from a full display name
 * @param fullName - The full display name (e.g., "John Doe")
 * @returns The first name (e.g., "John")
 */
const extractFirstName = (fullName: string): string => {
  return fullName.split(' ')[0] || fullName;
};

/**
 * Modern MSAL React hook for retrieving user information
 * 
 * Uses official MSAL React hooks within the provider context to extract
 * user information from authenticated accounts. Follows React best practices
 * and avoids legacy direct instance access patterns.
 * 
 * @returns UserInfo object with display name (first name only), email, and authentication status
 */
export const useUserInfo = (): UserInfo => {
  const { accounts } = useMsal();
  
  if (accounts.length > 0) {
    const account = accounts[0];
    
    // Priority: account.name -> email prefix -> "User"
    let displayName = 'User';
    
    if (account.name) {
      // Extract first name from full display name
      displayName = extractFirstName(account.name);
    } else if (account.username) {
      // Fallback to email prefix
      displayName = account.username.split('@')[0];
    }
    
    return {
      displayName,
      email: account.username || '',
      isAuthenticated: true
    };
  }
  
  return {
    displayName: 'User',
    email: '',
    isAuthenticated: false
  };
};

export type { UserInfo };